import type { LocalVault } from '@signbento/storage'

/**
 * Who is signed in on this device, in words a person recognises (findings A015/A124).
 *
 * The account id is an internal key and is never shown. The email the account signed in with is
 * kept beside the plan in `account.summary`, which sign-out and account switching already clear
 * (`ACCOUNT_SCOPED_SETTING_KEYS`), so it never outlives the account it names.
 */

export const ACCOUNT_ID_SETTING = 'account.current_id'
export const ACCOUNT_SUMMARY_SETTING = 'account.summary'

export interface AccountIdentity {
  readonly account_id: string | null
  readonly email: string | null
}

function summaryEmail(summary: unknown): string | null {
  if (typeof summary !== 'object' || summary === null || !('email' in summary)) return null
  const email = summary.email
  return typeof email === 'string' && email.includes('@') ? email : null
}

export async function readAccountIdentity(vault: LocalVault): Promise<AccountIdentity> {
  const [accountId, summary] = await Promise.all([
    vault.getSetting<string>(ACCOUNT_ID_SETTING),
    vault.getSetting<unknown>(ACCOUNT_SUMMARY_SETTING),
  ])
  return {
    account_id: typeof accountId === 'string' && accountId.length > 0 ? accountId : null,
    email: summaryEmail(summary),
  }
}

/** Records the account's email beside whatever the summary already holds. */
export async function rememberAccountEmail(vault: LocalVault, email: string): Promise<void> {
  const summary = await vault.getSetting<unknown>(ACCOUNT_SUMMARY_SETTING)
  if (summaryEmail(summary) === email) return
  const base = typeof summary === 'object' && summary !== null ? summary : {}
  await vault.putSetting(ACCOUNT_SUMMARY_SETTING, { ...base, email })
  announceAccountIdentity()
}

const IDENTITY_EVENT = 'signbento:account-identity'

/**
 * Says the signed-in identity was just written. Sign-in writes it only after the account's vault
 * has opened and the shell has already read an empty one, so the shell listens for this instead
 * of waiting for a reload.
 */
export function announceAccountIdentity(): void {
  if (typeof window !== 'undefined') window.dispatchEvent(new Event(IDENTITY_EVENT))
}

export function onAccountIdentity(listener: () => void): () => void {
  if (typeof window === 'undefined') return () => undefined
  window.addEventListener(IDENTITY_EVENT, listener)
  return () => window.removeEventListener(IDENTITY_EVENT, listener)
}
